<template>
    <o-modal :modalClass="'modal-xl'" @close="closeModal">
        <template v-slot:header>
            <h5 class="mb-0 font-weight-bold"><i class="fas fa-clock primary-text me-2"></i> Pending Members</h5>
        </template>
        <template v-slot:body>
            <!-- Search Control (Tabs + Input) -->
            <div class="search-box-custom mb-3">
                <div class="search-type-tabs">
                    <button
                        v-for="option in searchOptions"
                        :key="option.value"
                        class="tab-btn"
                        :class="{ active: searchBy.selected === option.value }"
                        @click="selectSearchType(option.value)"
                    >
                        {{ option.text }}
                    </button>
                </div>
                <div class="search-input-wrapper" v-if="searchBy.selected === 'email'">
                    <input
                        type="text"
                        class="form-control-search"
                        v-model="searchBy.criteria"
                        placeholder="Search by email..."
                        @keyup.enter="applyFilter()"
                    />
                    <button v-if="!isSearchActive" class="btn-search" @click="applyFilter()">
                        <i class="fas fa-search"></i>
                    </button>
                    <button v-else class="btn-search" @click="clearFilter()">
                        <i class="fas fa-times text-danger"></i>
                    </button>
                </div>
                <div class="search-input-wrapper" v-if="searchBy.selected === 'email_verified'">
                    <select class="form-select-custom" v-model="searchBy.criteria" @change="applyFilter()">
                        <option value="">All Status</option>
                        <option value="verified">Verified</option>
                        <option value="not_verified">Not Verified</option>
                    </select>
                    <button v-if="isSearchActive" class="btn-search" @click="clearFilter()">
                        <i class="fas fa-times text-danger"></i>
                    </button>
                </div>
            </div>


            <!-- Bulk Action Buttons -->
            <div v-if="selectedIds.length > 0" class="d-flex justify-content-between align-items-center mb-3">
                <span class="text-muted">
                    {{ selectedIds.length }} member's selected
                    <span v-if="isAllSelected" class="badge-all-selected">All pages</span>
                    <span v-else class="badge-page-selected">This page</span>
                </span>
                <div class="d-flex gap-2">
                    <button v-if="writeRegisterOption" class="btn btn-sm btn-approve-bulk" @click="handleBulkApprove()" :disabled="isProcessing">
                        <i class="fas fa-check-double me-1"></i> Approve Selected ({{ selectedIds.length }})
                    </button>
                    <button v-if="writeRegisterOption" class="btn btn-sm btn-delete-bulk" @click="handleBulkDelete()" :disabled="isProcessing">
                        <i class="fas fa-trash-alt me-1"></i> Delete Selected ({{ selectedIds.length }})
                    </button>
                </div>
            </div>
            
            <div class="d-flex justify-content-center py-5" v-if="page.isLoading">
                <div class="spinner-border primary-text" role="status">
                    <span class="visually-hidden">Loading...</span>
                </div>
            </div>
            
            <div v-if="!page.isLoading && page.output">
                <o-table style="text-align:left;" class="table-hover pending-list-table">
                    <thead>
                    <tr>
                        <th class="fw-bolder text-muted select-all-th" scope="col">
                            <div class="select-all-wrapper">
                                <input type="checkbox" class="form-check-input" v-model="selectAll" @change="toggleSelectAll('toggle')" title="Select All" />
                                <div class="select-all-dropdown">
                                    <button type="button" class="select-all-caret" @click.stop="showSelectMenu = !showSelectMenu" title="Select All Options">
                                        <svg width="10" height="6" viewBox="0 0 10 6" fill="none">
                                            <path d="M1 1L5 5L9 1" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                                        </svg>
                                    </button>
                                    <div v-if="showSelectMenu" class="select-all-menu">
                                        <button type="button" @click="toggleSelectAll('page')">
                                            <i class="fas fa-file-alt me-2"></i> Select This Page
                                        </button>
                                        <button type="button" @click="toggleSelectAll('all')">
                                            <i class="fas fa-layer-group me-2"></i> Select All ({{ totalDataCount }} data)
                                        </button>
                                        <button type="button" @click="clearSelection()" v-if="selectedIds.length > 0">
                                            <i class="fas fa-times me-2"></i> Clear Selection
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </th>
                        <th class="fw-bolder text-muted" scope="col" v-for="(field, index) in fields" :key="index">
                        {{ field }}
                        </th>
                    </tr>
                    </thead>
                    <tbody>
                    <tr v-for="(item, index) in PendingMembers" :key="item.id" class="member-row-clickable">
                        <td>
                            <input type="checkbox" class="form-check-input" :value="item.id" v-model="selectedIds" />
                        </td>
                        <td>{{ (meta.page - 1) * meta.limit + index + 1 }}</td>
                        <td>{{ item.user.email }}</td>
                        <td>
                            <span class="status-badge" :class="item.user.email_verified_at ? 'status-verified' : 'status-unverified'">
                                {{ item.user.email_verified_at ? 'Verified' : 'Not Verified' }}
                            </span>
                        </td>
                        <td class="ml-3">
                        <div class="d-flex gap-2">
                            <button v-if="writeRegisterOption" @click="handleApprove(item.id)" class="btn btn-action btn-success-action" :disabled="processingId !== null">
                                <span v-if="processingId === item.id && processingAction === 'approve'" class="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
                                <i v-else class="fas fa-check me-1"></i> Approve
                            </button>
                            <button v-if="writeRegisterOption" @click="handleUnapprove(item.id)" class="btn btn-action btn-delete" :disabled="processingId !== null">
                                <span v-if="processingId === item.id && processingAction === 'delete'" class="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
                                <i v-else class="fas fa-trash-alt me-1"></i> Delete
                            </button>
                        </div>
                        </td>
                    </tr>
                    <tr v-if="PendingMembers.length === 0">
                        <td :colspan="fields.length + 1" class="text-center py-4">No pending member</td>
                    </tr>
                    </tbody>
                </o-table>
                <div class="container mt-4">
                    <o-pagination :page="meta.page" :total="meta.total" @callback="navPage" />
                </div>
            </div>
        </template>
    </o-modal>
</template>
  
  <script>
  import { computed, onMounted, onBeforeUnmount, reactive, ref } from 'vue';
  import store from '@/store/';
  import { OTable, OPagination, OModal } from '@/components/OmnihControl';
  import { useToast } from '@/composables/useToast';
  
  export default {
    props: {
      isVisible: {
        type: Boolean,
        required: true,
      },
      writeRegisterOption: {
        type: Boolean,
        required: true,
      },
    },
    setup(props, { emit }) {
      const page = reactive({
        isLoading: false,
        output: false,
      });
  
      const { showToast } = useToast();

      const processingId = ref(null);
      const processingAction = ref(null);
      const isProcessing = ref(false);
      const fields = ref(['No', 'Email', 'Email Verified Status', 'Action']);

      // Bulk selection state
      const selectedIds = ref([]);
      const selectAll = ref(false);
      const isAllSelected = ref(false);
      const showSelectMenu = ref(false);

      // Search state
      const searchOptions = ref([
          { text: 'Email', value: 'email' },
          { text: 'Status', value: 'email_verified' },
      ]);

      const searchBy = reactive({
          selected: 'email',
          criteria: '',
      });

      const isSearchActive = ref(false);
  
      const meta = computed(() => store.state.PendingMembers.meta);
  
      const PendingMembers = computed(() => store.state.PendingMembers.PendingMembers);

      const totalDataCount = computed(() => {
          return meta.value?.totalData?.total_data || PendingMembers.value.length;
      });
  
      const fetchPendingMembers = async (isBackgroundRefresh = false) => {
        if (!isBackgroundRefresh) {
            page.isLoading = true;
            page.output = false;
            selectedIds.value = [];
            selectAll.value = false;
            isAllSelected.value = false;
            showSelectMenu.value = false;
        }
        try {
          const businessProfile = await store.dispatch('Auth/getBusinessProfile');
          const filter = { corporate_id: businessProfile.corporate_id };
          store.commit('PendingMembers/add_filter', { filter });
          await store.dispatch('PendingMembers/readPendingMembers');
        } catch (err) {
          console.error(err);
        } finally {
          if (!isBackgroundRefresh) {
            page.isLoading = false;
            page.output = true;
          }
        }
      };

      const selectSearchType = (value) => {
          searchBy.selected = value;
          searchBy.criteria = '';
          isSearchActive.value = false;
      };

      const applyFilter = () => {
          const search = {};
          if (searchBy.selected && searchBy.criteria) {
              search[searchBy.selected] = searchBy.criteria;
          } else {
              return;
          }
          store.commit('PendingMembers/set_search', { search });
          store.commit('PendingMembers/reset_meta');
          isSearchActive.value = true;
          fetchPendingMembers();
      };

      const clearFilter = () => {
          searchBy.criteria = '';
          isSearchActive.value = false;
          store.commit('PendingMembers/clear_search');
          store.commit('PendingMembers/reset_meta');
          fetchPendingMembers();
      };

      const handleClickOutside = (e) => {
          if (!e.target.closest('.select-all-wrapper')) {
              showSelectMenu.value = false;
          }
      };
  
      onMounted(() => {
        fetchPendingMembers();
        document.addEventListener('click', handleClickOutside);
      });

      onBeforeUnmount(() => {
        document.removeEventListener('click', handleClickOutside);
        store.commit('PendingMembers/clear_search');
        store.commit('PendingMembers/reset_meta');
      });
  
      const closeModal = () => {
        emit('close');
    };
  
    const handleApprove = async (id) => {
        const data = { id: id };

        processingId.value = id;
        processingAction.value = 'approve';

        try {
            const response = await store.dispatch('PendingMembers/update', { data });
            const approvedEmail = response.data.data.email;
            showToast({
                type: 'success',
                title: 'Approved',
                message: `Approved member email: ${approvedEmail}.`
            });
        } catch (err) {
            console.error('Error approving member:', err);
            showToast({
                type: 'error',
                title: 'Error',
                message: err.response?.data?.message || err.message
            });
        } finally {
            await fetchPendingMembers(true);
            processingId.value = null;
            processingAction.value = null;
        }
    };

    const navPage = (newPage) => {
        const _meta = { ...meta.value, page: newPage }
        store.commit('PendingMembers/switch_page', { meta: _meta })
        fetchPendingMembers()
    }
  
    const handleUnapprove = async (id) => {
        const data = { id: id };

        processingId.value = id;
        processingAction.value = 'delete';

        try {
            const response = await store.dispatch('PendingMembers/delete', { data });
            const deletedEmail = response.data.data.user.email;
            showToast({
                type: 'success',
                title: 'Deleted',
                message: `Deleted member email: ${deletedEmail}.`
            });
        } catch (err) {
            console.error('Error deleting member:', err);
            showToast({
                type: 'error',
                title: 'Error',
                message: err.response?.data?.message || err.message
            });
        } finally {
            await fetchPendingMembers(true);
            processingId.value = null;
            processingAction.value = null;
        }
    };

    const toggleSelectAll = (mode) => {
        showSelectMenu.value = false;

        if (mode === 'all') {
            store.dispatch('PendingMembers/fetchAllPendingIds')
                .then((allIds) => {
                    selectedIds.value = allIds;
                    selectAll.value = true;
                    isAllSelected.value = true;
                })
                .catch((err) => {
                    console.error('Failed to fetch all IDs:', err);
                });
        } else if (mode === 'page') {
            selectedIds.value = PendingMembers.value.map(item => item.id);
            selectAll.value = true;
            isAllSelected.value = false;
        } else {
            if (selectAll.value) {
                store.dispatch('PendingMembers/fetchAllPendingIds')
                    .then((allIds) => {
                        selectedIds.value = allIds;
                        isAllSelected.value = true;
                    })
                    .catch((err) => {
                        console.error('Failed to fetch all IDs:', err);
                    });
            } else {
                selectedIds.value = [];
                isAllSelected.value = false;
            }
        }
    };

    const clearSelection = () => {
        selectedIds.value = [];
        selectAll.value = false;
        isAllSelected.value = false;
        showSelectMenu.value = false;
    };

    const handleBulkApprove = () => {
        if (selectedIds.value.length === 0) return;

        isProcessing.value = true;

        store.dispatch('PendingMembers/bulkApprove', selectedIds.value)
            .then((response) => {
                const data = response.data.data;
                showToast({
                    type: 'success',
                    title: 'Success',
                    message: `Approved ${data.total_approved} pending member's.`
                });
            })
            .catch((err) => {
                console.error('Error bulk approving:', err);
                showToast({
                    type: 'error',
                    title: 'Error',
                    message: err.response?.data?.message || 'Failed to approve members'
                });
            })
            .finally(() => {
                isProcessing.value = false;
                fetchPendingMembers();
            });
    };

    const handleBulkDelete = () => {
        if (selectedIds.value.length === 0) return;

        isProcessing.value = true;

        store.dispatch('PendingMembers/bulkDelete', selectedIds.value)
            .then((response) => {
                const data = response.data.data;
                showToast({
                    type: 'success',
                    title: 'Success',
                    message: `Deleted ${data.total_deleted} pending member's.`
                });
            })
            .catch((err) => {
                console.error('Error bulk deleting:', err);
                showToast({
                    type: 'error',
                    title: 'Error',
                    message: err.response?.data?.message || 'Failed to delete members'
                });
            })
            .finally(() => {
                isProcessing.value = false;
                fetchPendingMembers();
            });
    };
  
      return {
        page,
        fields,
        meta,
        PendingMembers,
        closeModal,
        handleApprove,
        handleUnapprove,
        navPage,

        processingId,
        processingAction,
        isProcessing,
        selectedIds,
        selectAll,
        isAllSelected,
        showSelectMenu,
        totalDataCount,
        toggleSelectAll,
        clearSelection,
        handleBulkApprove,
        handleBulkDelete,
        searchOptions,
        searchBy,
        isSearchActive,
        selectSearchType,
        applyFilter,
        clearFilter,
      };
    },
    components: {
      OModal,
      OTable,
      OPagination,
    },
  };
  </script>
  
  <style scoped>
  .pending-list-table {
      white-space: nowrap;
      border: 1px solid #e2e8f0;
      border-radius: 12px;
      overflow: hidden;
      table-layout: fixed;
      width: 100%;
  }

  .pending-list-table th,
  .pending-list-table td {
      padding: 8px 10px !important;
      vertical-align: middle;
      text-overflow: ellipsis;
      font-size: 0.8rem;
  }

  .member-row-clickable {
      transition: background-color 0.2s;
  }

  .member-row-clickable:hover {
      background-color: #f8fafc !important;
  }

  .status-badge {
      padding: 0.25rem 0.75rem;
      border-radius: 20px;
      font-size: 0.75rem;
      font-weight: 500;
  }

  .status-verified {
      background: #ecfdf5;
      color: #065f46;
  }

  .status-unverified {
      background: #fffbeb;
      color: #92400e;
  }

  .btn-action {
      padding: 6px 14px;
      font-size: 12px;
      font-weight: 600;
      border-radius: 8px;
      transition: all 0.2s ease;
      display: inline-flex;
      align-items: center;
      border: none;
      cursor: pointer;
  }

  .btn-success-action {
      background-color: #ecfdf5;
      color: #065f46;
  }

  .btn-success-action:hover {
      background-color: #d1fae5;
      color: #064e3b;
  }

  .btn-delete {
      background-color: #fff1f2;
      color: #9f1239;
  }

  .btn-delete:hover {
      background-color: #ffe4e6;
      color: #be123c;
  }

  .primary-text {
      color: var(--primary-color, #F93E3E) !important;
  }

  /* Custom Search Box */
  .search-box-custom {
      display: flex;
      flex-direction: column;
      gap: 8px;
  }

  .search-type-tabs {
      display: flex;
      gap: 4px;
      background: #f1f5f9;
      padding: 4px;
      border-radius: 8px;
      width: fit-content;
  }

  .tab-btn {
      border: none;
      background: transparent;
      padding: 6px 16px;
      font-size: 13px;
      font-weight: 500;
      color: #64748b;
      border-radius: 6px;
      transition: all 0.2s;
      cursor: pointer;
  }

  .tab-btn.active {
      background: white;
      color: var(--primary-color, #F93E3E);
      box-shadow: 0 1px 3px rgba(0,0,0,0.1);
      font-weight: 600;
  }

  .search-input-wrapper {
      display: flex;
      align-items: center;
      gap: 8px;
      position: relative;
      background: white;
      border: 1px solid #e2e8f0;
      border-radius: 8px;
      padding: 4px;
      transition: all 0.2s;
  }

  .search-input-wrapper:focus-within {
      border-color: var(--primary-color, #F93E3E);
      box-shadow: 0 0 0 2px color-mix(in srgb, var(--primary-color, #F93E3E), transparent 88%);
  }

  .form-control-search {
      border: none;
      padding: 8px 12px;
      font-size: 14px;
      width: 100%;
      outline: none;
      background: transparent;
      color: #334155;
  }

  .form-control-search::placeholder {
      color: #94a3b8;
  }

  .form-select-custom {
      border: none;
      padding: 8px 12px;
      font-size: 14px;
      width: 100%;
      outline: none;
      background: transparent;
      color: #334155;
      cursor: pointer;
  }

  .btn-search {
      background: #f8fafc;
      border: none;
      width: 36px;
      height: 36px;
      border-radius: 6px;
      color: #64748b;
      display: flex;
      align-items: center;
      justify-content: center;
      cursor: pointer;
      transition: all 0.2s;
      flex-shrink: 0;
  }

  .btn-search:hover {
      background: #e2e8f0;
      color: var(--primary-color, #F93E3E);
  }

  /* Checkbox Styles */
  .form-check-input {
      cursor: pointer;
      width: 16px;
      height: 16px;
      border: 2px solid #cbd5e1;
      border-radius: 4px;
  }

  .form-check-input:checked {
      background-color: var(--primary-color, #F93E3E);
      border-color: var(--primary-color, #F93E3E);
  }

  .form-check-input:focus {
      border-color: var(--primary-color, #F93E3E);
      box-shadow: 0 0 0 0.2rem rgba(var(--primary-color, 249, 62, 62), 0.25);
  }

  /* Select All Dropdown */
  .select-all-th {
      position: relative;
  }

  .select-all-wrapper {
      display: flex;
      align-items: center;
      gap: 2px;
  }

  .select-all-caret {
      background: none;
      border: none;
      padding: 2px 4px;
      cursor: pointer;
      color: #94a3b8;
      border-radius: 4px;
      display: flex;
      align-items: center;
      transition: all 0.15s ease;
  }

  .select-all-caret:hover {
      color: #475569;
      background: #f1f5f9;
  }

  .select-all-menu {
      position: absolute;
      top: 100%;
      left: 0;
      z-index: 50;
      background: white;
      border: 1px solid #e2e8f0;
      border-radius: 8px;
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12);
      min-width: 220px;
      padding: 4px;
      margin-top: 4px;
  }

  .select-all-menu button {
      display: flex;
      align-items: center;
      width: 100%;
      padding: 8px 12px;
      border: none;
      background: none;
      font-size: 0.8rem;
      color: #334155;
      cursor: pointer;
      border-radius: 6px;
      transition: background 0.15s ease;
      text-align: left;
  }

  .select-all-menu button:hover {
      background: #f1f5f9;
  }

  .select-all-menu button i {
      color: #64748b;
      width: 16px;
      text-align: center;
  }

  /* Bulk Action Buttons */
  .btn-approve-bulk {
      background-color: var(--primary-color, #F93E3E);
      color: #fff;
      border: none;
      border-radius: 8px;
      font-weight: 600;
      padding: 6px 16px;
      font-size: 13px;
      transition: all 0.2s ease;
  }

  .btn-approve-bulk:hover {
      filter: brightness(0.9);
      color: var(--primary-color, #F93E3E);
      background-color: var(--secondary-color, #F93E3E);
  }

  .btn-approve-bulk:disabled {
      opacity: 0.5;
      cursor: not-allowed;
  }

  .btn-delete-bulk {
      background-color: #fff1f2;
      color: #9f1239;
      border: none;
      border-radius: 8px;
      font-weight: 600;
      padding: 6px 16px;
      font-size: 13px;
      transition: all 0.2s ease;
  }

  .btn-delete-bulk:hover {
      background-color: #ffe4e6;
      color: #be123c;
  }

  .btn-delete-bulk:disabled {
      opacity: 0.5;
      cursor: not-allowed;
  }

  /* Selection Scope Badges */
  .badge-all-selected,
  .badge-page-selected {
      display: inline-block;
      padding: 2px 8px;
      border-radius: 10px;
      font-size: 0.7rem;
      font-weight: 600;
      margin-left: 6px;
  }

  .badge-all-selected {
      background: color-mix(in srgb, var(--primary-color, #F93E3E), transparent 88%);
      color: var(--primary-color, #F93E3E);
  }

  .badge-page-selected {
      background: #f1f5f9;
      color: #64748b;
  }
  </style>
  
  <style>
  .omni-modal-container.modal-xl {
      min-width: 600px !important;
      max-width: 900px !important;
  }
  </style>